import dayjs from 'dayjs';
import type { Route } from '../../../types';
import type { RouteViewModel, RouteViewModelStop } from '../../../types/routeViewModel';
import type { CorrectionContext } from '../hooks/useCorrectionContext';
import {
  buildRouteStopPresentation,
  buildRouteStopPresentationContext
} from '../../routes/routeSummaryProjection';
import { resolveStopsReadOrder } from '../../sequentialStops/sequentialStopsOrdering';
import { getStopExecutionActualAt } from '../../routes/routeViewExecutionHelpers';
import { normalizeRouteStatus } from '../../../utils/statusUtils';

export type CorrectionRouteContextStopBand = 'COMPLETED' | 'CURRENT' | 'FUTURE' | 'UNKNOWN';

export type CorrectionRouteContextView = {
  routeNumber: string;
  statusLabel: string;
  carrierLabel: string | null;
  driverLabel: string | null;
  vehicleLabel: string | null;
  financeSummary: {
    lines: Array<{ label: string; value: string }>;
  };
  stops: Array<{
    ordinal: number;
    stopId: number | null;
    typeLabel: 'Погрузка' | 'Выгрузка';
    applicationLabel: string | null;
    groupLabel: string | null;
    addressLine: string;
    plannedLabel: string | null;
    actualLabel: string | null;
    statusLabel: string;
    band: CorrectionRouteContextStopBand;
  }>;
  executionTags: Array<{ text: string; color?: string }>;
};

const CIRCLED_ORDINALS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪', '⑫', '⑬', '⑭', '⑮'];

function formatPlanFact(iso: string | null | undefined): string | null {
  if (!iso || !String(iso).trim()) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  if (String(iso).includes('T')) {
    if (d.hour() === 0 && d.minute() === 0 && d.second() === 0) {
      return d.format('DD.MM');
    }
    return d.format('DD.MM HH:mm');
  }
  return d.format('DD.MM');
}

function partyName(party: Record<string, unknown> | null | undefined): string | null {
  if (!party) return null;
  const name = party.name ?? party.fullName ?? party.title;
  if (name != null && String(name).trim()) return String(name).trim();
  return null;
}

function resolveBand(
  ctx: CorrectionContext,
  stopId: number | null | undefined
): CorrectionRouteContextStopBand {
  if (stopId == null) return 'UNKNOWN';
  const band = ctx.getStopBand(Number(stopId));
  if (!band) return 'UNKNOWN';
  return band.band;
}

function bandStatusLabel(band: CorrectionRouteContextStopBand): string {
  switch (band) {
    case 'COMPLETED':
      return '✓ Выполнено';
    case 'CURRENT':
      return 'Текущая';
    case 'FUTURE':
      return 'Будущая';
    default:
      return '—';
  }
}

function applicationLabelFromPresentation(presentation: ReturnType<typeof buildRouteStopPresentation>): string | null {
  if (presentation.applicationNumber) {
    return `Заявка №${presentation.applicationNumber}`;
  }
  const pt = String(presentation.pointType || '').toLowerCase();
  if (pt === 'application' && presentation.sourceLabel) {
    return presentation.sourceLabel;
  }
  if (pt === 'warehouse' && presentation.sourceLabel) {
    return presentation.sourceLabel;
  }
  return null;
}

function resolveGroupLabel(
  view: RouteViewModel,
  stop: RouteViewModelStop
): string | null {
  const groups = view.structure.applicationGroups ?? [];
  if (groups.length <= 1) return null;
  const pt = String(stop.pointType || '').toLowerCase();
  if (pt !== 'application' || stop.pointId == null) return null;
  const appId = Number(stop.pointId);
  const match = groups.find((g) => g.applicationId != null && Number(g.applicationId) === appId);
  return match?.label?.trim() || null;
}

function buildFinanceSummary(view: RouteViewModel): CorrectionRouteContextView['financeSummary'] {
  const lines: Array<{ label: string; value: string }> = [];
  const agg = view.finance?.aggregatedStatus;
  if (agg) {
    lines.push({ label: 'Общий статус', value: String(agg.overallStatus) });
    lines.push({ label: 'Перевозчик', value: String(agg.payableStatus) });
    lines.push({ label: 'Клиент', value: String(agg.receivableStatus) });
  }
  const payable = view.finance?.payable;
  if (payable) {
    lines.push({
      label: 'Остаток перевозчику',
      value: `${payable.remaining.toLocaleString('ru-RU')} ₽`
    });
  }
  if (view.carrierFinance?.rateAmount != null) {
    lines.push({
      label: 'Ставка',
      value: `${view.carrierFinance.rateAmount.toLocaleString('ru-RU')} ₽`
    });
  }
  return { lines };
}

export function buildCorrectionRouteContextView(params: {
  route: Route;
  view: RouteViewModel;
  ctx: CorrectionContext;
}): CorrectionRouteContextView {
  const { route, view, ctx } = params;
  const rawStops = view.structure?.stops ?? [];
  const ordered = resolveStopsReadOrder(rawStops);

  const presentationCtx = buildRouteStopPresentationContext({
    applications: view.applications as Parameters<typeof buildRouteStopPresentationContext>[0]['applications'],
    loadingWarehouse: view.operation.loadingWarehouse as Parameters<
      typeof buildRouteStopPresentationContext
    >[0]['loadingWarehouse'],
    unloadingWarehouse: view.operation.unloadingWarehouse as Parameters<
      typeof buildRouteStopPresentationContext
    >[0]['unloadingWarehouse'],
    routeConsigneeName: (view.parties.consignee as { name?: string } | null)?.name ?? null,
    routeLoadingCity: view.operation.direction?.loadingCity ?? route.loadingCity ?? null,
    routeUnloadingCity: view.operation.direction?.unloadingCity ?? route.unloadingCity ?? null
  });

  const stops = ordered.map((stop, index) => {
    const presentation = buildRouteStopPresentation(stop, presentationCtx);
    const stopId = stop.id != null ? Number(stop.id) : null;
    const band = resolveBand(ctx, stopId);
    const typeRaw = String(stop.type ?? '').toLowerCase();
    const typeLabel: 'Погрузка' | 'Выгрузка' =
      typeRaw === 'loading' ? 'Погрузка' : 'Выгрузка';

    const addressLine =
      [presentation.displayCity, presentation.primaryLine].filter((x) => x && x !== '—').join(', ') ||
      presentation.destinationLabel ||
      '—';

    const plannedLabel = formatPlanFact(stop.plannedAt as string | null | undefined);
    const actualLabel = formatPlanFact(getStopExecutionActualAt(stop) ?? (stop.actualAt as string | null));

    return {
      ordinal: index + 1,
      stopId,
      typeLabel,
      applicationLabel: applicationLabelFromPresentation(presentation),
      groupLabel: resolveGroupLabel(view, stop),
      addressLine,
      plannedLabel,
      actualLabel,
      statusLabel: bandStatusLabel(band),
      band
    };
  });

  const driver = view.parties.driver as Record<string, unknown> | null | undefined;
  const driverLabel =
    partyName(driver) ||
    [driver?.lastName, driver?.firstName].filter(Boolean).join(' ').trim() ||
    null;

  const vehicle = view.parties.vehicle as Record<string, unknown> | null | undefined;
  const trailer = view.parties.trailerVehicle as Record<string, unknown> | null | undefined;
  const vehicleParts = [partyName(vehicle), trailer ? `Прицеп: ${partyName(trailer)}` : null].filter(
    Boolean
  ) as string[];

  const executionTags: CorrectionRouteContextView['executionTags'] = [];
  if (ctx.correction.executionLock.inTransit) {
    executionTags.push({ text: 'В пути', color: 'orange' });
  }
  if (ctx.correction.executionLock.executionComplete) {
    executionTags.push({ text: 'Исполнение завершено', color: 'green' });
  }

  return {
    routeNumber: route.number?.trim() ? route.number : String(route.id),
    statusLabel: normalizeRouteStatus(view.operation.status) || String(view.operation.status),
    carrierLabel: partyName(view.parties.carrier as Record<string, unknown> | null | undefined),
    driverLabel,
    vehicleLabel: vehicleParts.length ? vehicleParts.join(' · ') : null,
    financeSummary: buildFinanceSummary(view),
    stops,
    executionTags
  };
}

export function formatCircledOrdinal(index: number): string {
  if (index >= 0 && index < CIRCLED_ORDINALS.length) return CIRCLED_ORDINALS[index];
  return String(index + 1);
}
